import { useState } from 'react';
import { Panel } from '../whoop-panel';
import { GOAL } from '../whoop-goal';
import { C } from '../whoop-theme';

interface Day {
  dow: string;
  date: string;
  type: string;
  title: string;
  detail: string;
  status: string;
  strain?: string;
}

const BASE: Day[] = [
  {
    dow: 'Mon',
    date: 'Jun 29',
    type: 'Rest',
    title: 'Rest day',
    detail: 'Full recovery',
    status: 'rest',
  },
  {
    dow: 'Tue',
    date: 'Jun 30',
    type: 'Easy',
    title: 'Easy run',
    detail: '45 min · 9:10/mi',
    status: 'done',
    strain: '9.4',
  },
  {
    dow: 'Wed',
    date: 'Jul 1',
    type: 'Quality',
    title: 'Threshold',
    detail: 'Adapted → 2×8 min',
    status: 'today',
    strain: '~12',
  },
  {
    dow: 'Thu',
    date: 'Jul 2',
    type: 'Easy',
    title: 'Easy / cross',
    detail: '40 min aerobic',
    status: 'planned',
  },
  {
    dow: 'Fri',
    date: 'Jul 3',
    type: 'Rest',
    title: 'Rest day',
    detail: 'Mobility optional',
    status: 'rest',
  },
  {
    dow: 'Sat',
    date: 'Jul 4',
    type: 'Long',
    title: 'Long run',
    detail: '16 mi · 8:55/mi',
    status: 'planned',
    strain: '~17',
  },
  {
    dow: 'Sun',
    date: 'Jul 5',
    type: 'Easy',
    title: 'Recovery',
    detail: '30 min very easy',
    status: 'planned',
  },
];

const STATUS_COLOR: Record<string, string> = {
  rest: C.text3,
  done: C.good,
  today: C.accent,
  planned: C.text2,
  adapted: C.warn,
};

const LOAD = [
  { k: 'Runs planned', v: '5' },
  { k: 'Weekly mileage', v: '38 mi' },
  { k: 'Hard sessions', v: '2' },
  { k: 'Long run', v: '16 mi' },
  { k: 'Recovery days', v: '2' },
  { k: 'Projected strain', v: 'Mod–high' },
];

const WEEKS = [
  { label: 'Wk 1', mileage: '34', phase: 'Base', pct: 62, current: false },
  { label: 'Wk 2', mileage: '38', phase: 'Volume', pct: 70, current: true },
  { label: 'Wk 3', mileage: '42', phase: 'Volume', pct: 78, current: false },
  { label: 'Wk 4', mileage: '30', phase: 'Down', pct: 55, current: false },
];

function swapWedFri(days: Day[]): Day[] {
  const copy = days.map((d) => ({ ...d }));
  const wed = copy[2];
  const fri = copy[4];
  if (wed && fri) {
    copy[2] = { ...fri, dow: 'Wed', date: 'Jul 1' };
    copy[4] = { ...wed, dow: 'Fri', date: 'Jul 3' };
  }
  return copy;
}

export function PlanPage() {
  const [swapped, setSwapped] = useState(false);
  const days = swapped ? swapWedFri(BASE) : BASE;

  const balance = [
    {
      color: C.good,
      mark: '✓',
      text: 'Easy / hard ratio is healthy — 3 aerobic days support 2 quality sessions.',
    },
    {
      color: C.good,
      mark: '✓',
      text: 'Long run is progressing on schedule (+1 mi from last week).',
    },
    {
      color: C.warn,
      mark: '!',
      text: swapped
        ? 'Threshold now sits the day before the long run — quality legs may be tired.'
        : 'Threshold is 3 days before the long run — spacing is adequate.',
    },
    {
      color: C.warn,
      mark: '!',
      text: 'No dedicated strength session this week. Optional, but worth one short circuit.',
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.025em', margin: 0 }}>
          Plan
        </h1>
        <div style={{ fontSize: 14, color: C.text2, marginTop: 4 }}>
          What I&apos;m doing next — and whether the plan holds together.
        </div>
      </div>

      {/* Goal header */}
      <Panel
        style={{
          padding: '22px 24px',
          marginBottom: 18,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '22px 36px',
          alignItems: 'center',
        }}
      >
        <div style={{ flex: 1, minWidth: 210 }}>
          <div
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: 10,
              letterSpacing: '0.16em',
              color: C.accent,
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            Goal
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' }}>
            {GOAL.title}
          </div>
          <div style={{ fontSize: 13, color: C.text2, marginTop: 4 }}>
            {GOAL.event} · {GOAL.date}
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '26px 30px' }}>
          <GoalStat label="Target pace" value={GOAL.targetPace} mono />
          <GoalStat label="Current est." value={GOAL.currentEstimate} mono />
          <GoalStat label="Confidence" value={GOAL.confidence} />
          <GoalStat label="Weeks left" value={String(GOAL.weeksRemaining)} mono />
          <GoalStat label="Phase" value={GOAL.phase} color={C.accent} />
        </div>
      </Panel>

      {/* 4-week strip */}
      <Panel style={{ padding: '20px 22px', marginBottom: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 16 }}>
          Block overview · next 4 weeks
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4,1fr)',
            gap: 14,
            alignItems: 'end',
          }}
        >
          {WEEKS.map((w) => (
            <div key={w.label} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ height: 80, display: 'flex', alignItems: 'flex-end' }}>
                <div
                  style={{
                    width: '100%',
                    height: `${w.pct}%`,
                    background: w.current ? 'rgba(181,155,255,0.28)' : 'rgba(255,255,255,0.06)',
                    borderRadius: 6,
                    border: `1px solid ${w.current ? 'rgba(181,155,255,0.5)' : 'rgba(255,255,255,0.1)'}`,
                  }}
                />
              </div>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}
              >
                <span
                  style={{ fontSize: 12, fontWeight: 700, color: w.current ? C.accent : C.text }}
                >
                  {w.label}
                </span>
                <span
                  style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", color: C.text2 }}
                >
                  {w.mileage}mi
                </span>
              </div>
              <div style={{ fontSize: 11, color: C.text3 }}>{w.phase}</div>
            </div>
          ))}
        </div>
      </Panel>

      {/* This week */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 700 }}>This week</div>
        <button
          type="button"
          onClick={() => setSwapped((value) => !value)}
          className="ww-btn"
          style={{
            fontFamily: 'inherit',
            fontSize: '12.5px',
            fontWeight: 600,
            cursor: 'pointer',
            borderRadius: 8,
            padding: '8px 13px',
            border: '1px solid rgba(255,255,255,0.14)',
            background: 'rgba(255,255,255,0.05)',
            color: C.text,
          }}
        >
          ⇄ Swap Wed / Fri
        </button>
      </div>
      <div
        style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 9, marginBottom: 14 }}
      >
        {days.map((d) => {
          const color = STATUS_COLOR[d.status] ?? C.text2;
          const badge =
            d.status === 'done'
              ? '✓ done'
              : d.status === 'today'
                ? 'today'
                : d.status === 'rest'
                  ? 'rest'
                  : 'planned';
          return (
            <div
              key={`${d.dow}-${d.date}`}
              style={{
                border: `1px solid ${d.status === 'today' ? 'rgba(181,155,255,0.35)' : 'rgba(255,255,255,0.08)'}`,
                background: d.status === 'today' ? 'rgba(181,155,255,0.1)' : C.panel,
                borderRadius: 12,
                overflow: 'hidden',
                minHeight: 148,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div style={{ height: 3, background: color }} />
              <div
                style={{
                  padding: '11px 11px 13px',
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: 9,
                  }}
                >
                  <span style={{ fontSize: 12, fontWeight: 700 }}>{d.dow}</span>
                  <span
                    style={{
                      fontSize: 10,
                      color: C.text3,
                      fontFamily: "'JetBrains Mono',monospace",
                    }}
                  >
                    {d.date}
                  </span>
                </div>
                <div style={{ fontSize: '13.5px', fontWeight: 700, marginBottom: 3 }}>
                  {d.title}
                </div>
                <div style={{ fontSize: '11.5px', color: C.text2, lineHeight: 1.35, flex: 1 }}>
                  {d.detail}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    fontFamily: "'JetBrains Mono',monospace",
                    color,
                    marginTop: 8,
                  }}
                >
                  {badge}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {swapped && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            padding: '13px 16px',
            background: 'rgba(227,198,115,0.09)',
            border: '1px solid rgba(227,198,115,0.28)',
            borderRadius: 11,
            fontSize: 13,
            color: '#e9d49a',
            marginBottom: 22,
          }}
        >
          <span style={{ fontWeight: 700 }}>⤳</span>
          Swapped. Recovery spacing before Saturday is now tighter — consider moving the long run to
          Sunday to protect quality.
        </div>
      )}

      {/* Load + balance */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.25fr', gap: 18, marginTop: 8 }}>
        <Panel style={{ padding: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 16 }}>Weekly load</div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {LOAD.map((l) => (
              <div
                key={l.k}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '9px 0',
                  borderTop: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <span style={{ fontSize: 13, color: '#cbc9c2' }}>{l.k}</span>
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    fontFamily: "'JetBrains Mono',monospace",
                  }}
                >
                  {l.v}
                </span>
              </div>
            ))}
          </div>
        </Panel>
        <Panel style={{ padding: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 16 }}>Training balance</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
            {balance.map((b) => (
              <div
                key={b.text}
                style={{
                  display: 'flex',
                  gap: 11,
                  alignItems: 'flex-start',
                  fontSize: 13,
                  color: '#cbc9c2',
                  lineHeight: 1.45,
                }}
              >
                <span
                  style={{
                    width: 20,
                    height: 20,
                    flex: 'none',
                    borderRadius: 6,
                    background: 'rgba(255,255,255,0.05)',
                    color: b.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  {b.mark}
                </span>
                {b.text}
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function GoalStat({
  label,
  value,
  mono,
  color,
}: {
  label: string;
  value: string;
  mono?: boolean;
  color?: string;
}) {
  return (
    <div>
      <div style={{ fontSize: 11, color: C.text3, marginBottom: 4 }}>{label}</div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 700,
          fontFamily: mono ? "'JetBrains Mono',monospace" : undefined,
          color,
        }}
      >
        {value}
      </div>
    </div>
  );
}
